import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import DisplayDateTime from '../../components/DisplayDateTime';
import FeedbackProgress from './FeedbackProgress/FeedbackProgress';
import GlobalModal from '../../components/GlobalModal';
import ResolveModal from './ResolveModal';
import WithdrawModal from './WithdrawModal';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';

// TYPES
import { FeedbackActionsAction, FeedbackFetchFeedback } from '../../api/models';
import { RouteParams } from '../../Interfaces';

const FeedbackDetail: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [feedbackDetailFormErrorMessage, setFeedbackDetailFormErrorMessage] = React.useState<string>('');

	const [feedbackProgressFormErrorMessage, setFeedbackProgressFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get Feedback ID from params
	const params = useParams<RouteParams>();
	const feedbackId = params.id;

	// Set up data state and initial data
	const initialFeedbackDetailData: FeedbackFetchFeedback = {
		id: undefined,
		identifier: undefined,
		subject: undefined,
		category: undefined,
		status: undefined,
		categoryDescription: undefined,
		statusDescription: undefined,
		creator: undefined,
		occurrence: undefined,
		summary: undefined,
		summaryUrl: undefined,
		attachments: [],
		isResponseRequired: undefined,
		isAnonymous: undefined,
		anonymousContact: undefined,
		canViewActions: undefined,
		canWithdraw: undefined
	};

	// State to control modal
	const [showResolveModal, setShowResolveModal] = React.useState<boolean>(false);

	const [showWithdrawModal, setShowWithdrawModal] = React.useState<boolean>(false);

	// Set up Feedback Detail Data state
	const [feedbackDetailData, setFeedbackDetailData] = React.useState<FeedbackFetchFeedback>(initialFeedbackDetailData);

	const getFeedbackDetailsData = async (feedbackId: string) => {
		setIsLoading(true);

		const response = await apibridge.getFeedbackDetailsData(feedbackId);

		if (response.isError) {
			setIsLoading(false);

			setFeedbackDetailFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialFeedbackDetailData } = response;

			setIsLoading(false);

			setFeedbackDetailData(result);
		}
	};

	// Set up Feedback Progress Data state
	const [feedbackProgressData, setFeedbackProgressData] = React.useState<FeedbackActionsAction[]>([]);

	const getFeedbackProgress = async (id: string | undefined) => {
		if (id) {
			setIsLoading(true);

			const response = await apibridge.getFeedbackProgress(id);

			if (response.isError) {
				setIsLoading(false);

				setFeedbackProgressFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				const { result = [] } = response;

				setIsLoading(false);

				setFeedbackProgressData(result);
			}
		}
	};

	// Combine 2 api call to hide and show loading spinner at the same time
	const getFeedbackDetailDashboardData = async () => {
		setIsLoading(true);

		await getFeedbackDetailsData(feedbackId);

		await getFeedbackProgress(feedbackId);

		setIsLoading(false);
	};

	React.useEffect(() => {
		getFeedbackDetailDashboardData();
	}, []);

	const {
		id,
		identifier,
		resolver,
		subject,
		creator,
		category,
		status,
		categoryDescription,
		statusDescription,
		occurrence,
		summary,
		summaryUrl,
		attachments,
		isResponseRequired,
		isAnonymous,
		anonymousContact,
		canUpdate,
		canViewActions,
		canWithdraw
	} = feedbackDetailData;

	return (
		<div className="page-general-layout complaints-detail-page">
			{!!feedbackDetailFormErrorMessage ? (
				<div className="form-validation-error">{feedbackDetailFormErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<h1 className="header-offset">{subject}</h1>

								<div className="complaints-detail-general-info">
									<div className="general-info-left">
										<div className="info-col">
											<div className="info-title">Submitted by</div>
											<div className="info-detail">{creator}</div>
										</div>

										<div className="info-col">
											<div className="info-title">FeedbackID</div>
											<div className="info-detail">{identifier}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Category</div>
											<div className="info-detail">{categoryDescription}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Status</div>
											<div className="info-detail">
												{resolver ? (
													<>
														Resolved by <strong>{resolver}</strong>{' '}
													</>
												) : (
													<>{statusDescription}</>
												)}
											</div>
										</div>
									</div>

									<div className="general-info-right">
										{canWithdraw && (
											<Button className="btn-primary" onClick={() => setShowWithdrawModal(true)}>
												Withdraw
											</Button>
										)}

										{isOrganisationActive && canUpdate && (
											<Button className="btn-primary resolve-btn mleft-10" onClick={() => setShowResolveModal(true)}>
												Mark as resolved
											</Button>
										)}
									</div>
								</div>

								<div className="two-col-layout">
									<div className="complaints-detail">
										{isAnonymous && anonymousContact && (
											<div className="detail-block">
												<h2 className="detail-heading">Submitted by</h2>
												<p className="detail-info">Anonymous ({anonymousContact})</p>
											</div>
										)}

										<div className="detail-block">
											<h2 className="detail-heading">Date and time of relevant event</h2>
											<time className="detail-info">
												<DisplayDateTime>{occurrence}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Feedback summary</h2>
											<p className="detail-info">{summary}</p>
										</div>

										{attachments && attachments.length > 0 && (
											<div className="detail-block">
												<h2 className="detail-heading">Attachments</h2>
												<ul>
													{attachments.map((item, index) => {
														return (
															<li key={index}>
																<a className="attachments-download-link" href={item.link} download>
																	<span className="link-name">{item.displayFileName}</span>
																	<span className="link-text d-none d-lg-block">Download</span>
																	<DownloadIcon className="link-icon d-block d-lg-none" />
																</a>
															</li>
														);
													})}
												</ul>
											</div>
										)}

										<div className="detail-block">
											<h2 className="detail-heading">Is a response required?</h2>
											<p className="detail-info">{isResponseRequired ? 'Yes' : 'No'}</p>
										</div>

										<a className="btn btn-secondary mtop-40 download-btn" href={`${summaryUrl}`} download>
											<DownloadIcon className="link-icon" />
											Download Feedback Summary
										</a>
									</div>

									{canViewActions && (
										<FeedbackProgress
											feedbackId={id}
											canUpdate={canUpdate}
											feedbackProgressData={feedbackProgressData}
											getFeedbackProgress={getFeedbackProgress}
											feedbackProgressFormErrorMessage={feedbackProgressFormErrorMessage}
											isLoading={isLoading}
										></FeedbackProgress>
									)}

									<GlobalModal isOpen={showResolveModal} setIsOpen={setShowResolveModal} titleText="Mark as resolved">
										<ResolveModal feedbackOccurrence={occurrence} feedbackId={id} />
									</GlobalModal>

									<GlobalModal
										isOpen={showWithdrawModal}
										setIsOpen={setShowWithdrawModal}
										titleText="Withdraw feedback"
									>
										<WithdrawModal feedbackId={id} subject={subject} />
									</GlobalModal>
								</div>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default FeedbackDetail;
